
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';

import type { AsyncStatus } from '../../../../../@types/AsyncStatus';
import { setCompanyCommission } from '../../../../../store/AuthReducer';
import type { RootState } from '../../../../../store/store';
import { JWTAuthorization } from '../../../../../utils/JWTHeader';
import tailwindConfig from '../../../../../utils/tailwindColors';
import { showToast } from '../../../../common/Notification';
import Table from '../../../../common/Table';

interface CommData {
  [key: string]: React.ReactElement;
}

function CommSelector() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const dispatch = useDispatch();
  const [data, setData] = useState<CommData[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');

  const handleCommissionChange = (commission: number) => {
    if (!company || !jwt || !company.id) {return;}
    setStatus('PENDING');
    fetch(`/api/companies/${company.id}/market-settings`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: JWTAuthorization(jwt),
      },
      body: JSON.stringify({
        commission,
        realSales: company.realSales,
        nbMandates: company.nbMandates,
        nbPreSaleStatements: company.nbPreSaleStatements,
        pricePreSaleStatements: company.pricePreSaleStatements,
        nbSaleStatements: company.nbSaleStatements,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to update commission');
        }
        dispatch(setCompanyCommission(commission));
        setStatus('SUCCESS');
      })
      .catch((response_error) => {
        console.error('Error updating commission :', response_error);
        showToast('error', 'Une erreur est survenue lors de l\'enregistrement des données.');
        setStatus('SUCCESS');
      });
  };

  const formatData = (): CommData[] => {
    const commissions = [3, 4, 5, 6, 7, 8];
    const rowData: CommData = { 'QUELLE EST VOTRE COMMISSION ?': <span key="label">Sélectionnez votre commission :</span> };

    commissions.forEach((commission) => {
      rowData[`${commission}%`] = (
        <input
          key={`commission_${commission}`}
          type="radio"
          value={commission}
          checked={company.commission === commission}
          onChange={() => handleCommissionChange(commission)}
        />
      );
    });

    return [rowData];
  };

  useEffect(() => {
    if (!company || !jwt || !company.id) {return;}
    setData(formatData());
    setStatus('SUCCESS');
  }, [company, jwt]);

  const headers = ['QUELLE EST VOTRE COMMISSION ?', '3%', '4%', '5%', '6%', '7%', '8%'];

  return (
    <div>
      {status === 'PENDING' && (
        <div className="w-full text-center">
          <Table data={[]} headers={headers} tableClass="w-full" />
          <Skeleton count={1} height={30} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-2" />
        </div>
      )}
      {status === 'SUCCESS' && (
        <Table
          data={data}
          headers={headers}
          tableClass="w-full min-h-16"
          idKey="QUELLE EST VOTRE COMMISSION ?"
        />
      )}
    </div>
  );
}

export default CommSelector;
